import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Check, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Badge } from "../components/Pill";
import Select from "../components/Select";

const PERKS = [
  { value: "300+", label: "New followers every day", desc: "An audience that grows while you sleep." },
  { value: "5000+", label: "Couples featured", desc: "Real weddings, real reach, real enquiries." },
  { value: "3500+", label: "Global brands", desc: "From couture houses to corner ateliers." },
  { value: "$0", label: "Editorial features", desc: "If your work moves our editors, it costs nothing." },
];

const BRAND_PLANS = [
  {
    id: "Recommended Vendor",
    name: "Recommended Vendor",
    monthly: { price: "₹250", key: "recommended_monthly" },
    annual: { price: "₹3,000", key: "recommended_annual" },
    desc: "Get featured on the website as a recommended vendor in the TBOI directory.",
    perks: ["Directory listing with photo and links", "Enquiry form on your card", "Reviewed by our editors"],
  },
  {
    id: "Website + Instagram",
    name: "Website + Instagram",
    monthly: { price: "₹2,500", key: "web_ig_monthly" },
    annual: { price: "₹30,000", key: "web_ig_annual" },
    desc: "The directory listing plus a monthly presence on @thebridesofindia.",
    perks: ["Everything in Recommended Vendor", "1 feed post every month", "2 stories every month"],
  },
  {
    id: "Exclusive Feature",
    name: "Exclusive Feature",
    custom: true,
    desc: "An exclusive feature across the website and Instagram with original content.",
    perks: ["Everything in Website + Instagram", "Exclusive editorial content", "Priority coverage of your work"],
  },
];

const COUPLE_PLANS = [
  {
    id: "Editorial Feature",
    name: "Editorial Feature",
    free: true,
    desc: "Get featured editorially. Our editors pick the weddings that move them.",
    perks: ["Considered for The Edit", "Real wedding storytelling", "Always zero rupees"],
  },
  {
    id: "Full Coverage",
    name: "Full Coverage",
    oneTime: { price: "₹25,000", key: "full_coverage" },
    desc: "Complete pre and post wedding coverage of your celebration.",
    perks: ["1 exclusive feed post", "5 stories across the festivities", "Pre and post wedding moments"],
  },
  {
    id: "Exclusive Wedding Coverage",
    name: "Exclusive Coverage",
    oneTime: { price: "₹50,000", key: "exclusive_coverage" },
    desc: "The full TBOI treatment, reserved for a handful of weddings each season.",
    perks: ["Exclusive multi-post coverage", "Dedicated editorial storytelling", "Priority feature placement"],
  },
];

const KINDS = ["Brand", "Vendor", "Venue", "Couple"];

export default function GetFeatured() {
  const [params, setParams] = useSearchParams();
  const [audience, setAudience] = useState("brands");
  const [billing, setBilling] = useState("annual");
  const plans = audience === "brands" ? BRAND_PLANS : COUPLE_PLANS;
  const [plan, setPlan] = useState(BRAND_PLANS[0].id);
  const [form, setForm] = useState({ name: "", email: "", kind: "Brand", instagram: "", city: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [paidDone, setPaidDone] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (params.get("cancelled")) {
      toast.error("Payment cancelled. Your details are saved, you can try again anytime.");
      setParams({}, { replace: true });
      return;
    }
    if (!sessionId) return;
    setVerifying(true);
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts += 1;
      try {
        const s = await api(`/payments/status/${sessionId}`);
        if (s.payment_status === "paid") {
          clearInterval(poll);
          setVerifying(false);
          setPaidDone(true);
          setParams({}, { replace: true });
        } else if (attempts > 10) {
          clearInterval(poll);
          setVerifying(false);
          toast.error("Payment is still processing. We will confirm by email shortly.");
          setParams({}, { replace: true });
        }
      } catch {
        clearInterval(poll);
        setVerifying(false);
        setParams({}, { replace: true });
      }
    }, 2000);
    return () => clearInterval(poll);
  }, []);

  const switchAudience = (a) => {
    setAudience(a);
    setPlan((a === "brands" ? BRAND_PLANS : COUPLE_PLANS)[0].id);
    setForm((f) => ({ ...f, kind: a === "brands" ? "Brand" : "Couple" }));
  };

  const activePlan = plans.find((p) => p.id === plan);
  const lookupKey = () => {
    if (!activePlan) return null;
    if (activePlan.custom || activePlan.free) return null;
    if (activePlan.oneTime) return activePlan.oneTime.key;
    return billing === "annual" ? activePlan.annual.key : activePlan.monthly.key;
  };
  const priceLabel = () => {
    if (!activePlan) return "";
    if (activePlan.custom) return "Custom quote";
    if (activePlan.free) return "Free";
    if (activePlan.oneTime) return activePlan.oneTime.price;
    return billing === "annual" ? `${activePlan.annual.price} / year` : `${activePlan.monthly.price} / month`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const key = lookupKey();
      const planLabel = activePlan.oneTime || activePlan.custom || activePlan.free
        ? plan
        : `${plan} (${billing === "annual" ? "Annual" : "Monthly"})`;
      const lead = await api("/featured", { method: "POST", body: { ...form, plan: planLabel } });
      if (key) {
        const res = await api("/payments/checkout", {
          method: "POST",
          body: { lookup_key: key, origin_url: window.location.origin, lead_id: lead.id },
        });
        window.location.href = res.checkout_url;
        return;
      }
      setDone(true);
      toast.success("Request received. Our editors will be in touch.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full border-2 border-ink/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-gold bg-white transition-colors duration-300";
  const labelCls = "block text-[10px] font-bold uppercase tracking-[0.2em] text-gold mb-2";

  const planPrice = (p) => {
    if (p.custom) return { main: "Custom", sub: "quote" };
    if (p.free) return { main: "Free", sub: "forever" };
    if (p.oneTime) return { main: p.oneTime.price, sub: "one time" };
    return billing === "annual"
      ? { main: p.annual.price, sub: "/ year" }
      : { main: p.monthly.price, sub: "/ month" };
  };

  return (
    <main data-testid="get-featured-page">
      <section className="bg-blush pt-32 md:pt-36 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <Reveal><Badge tone="ink">Get Featured on TBOI</Badge></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display uppercase text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-ink leading-[0.98]">
              Celebrate the beauty of Indian weddings
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 max-w-2xl mx-auto text-neutral-600">
              India's leading wedding media platform showcasing beautiful couples, decor and artists, and helping
              brands connect with their dream audiences.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 md:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mb-16" data-testid="featured-perks">
          {PERKS.map((p, i) => (
            <Reveal key={p.label} delay={0.06 * i}>
              <div className="bg-white border-2 border-gold/10 rounded-[1.5rem] p-5 md:p-7 h-full hover:border-gold/40 transition-colors duration-300">
                <p className="font-display text-3xl md:text-5xl text-gold">{p.value}</p>
                <p className="mt-2 md:mt-3 text-[9px] md:text-[10px] font-bold uppercase tracking-[0.18em] text-ink">{p.label}</p>
                <p className="mt-2 text-xs text-neutral-500 leading-relaxed hidden sm:block">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="text-center">
          <Badge tone="gold">Choose Your Feature</Badge>
        </Reveal>
        <Reveal delay={0.05} className="text-center">
          <h2 className="mt-5 font-display uppercase text-3xl md:text-5xl text-ink leading-[0.98]">Pick a plan that fits</h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4" data-testid="audience-toggle">
            <div className="inline-flex bg-white border-2 border-ink/10 rounded-full p-1.5 gap-1">
              {[["brands", "Brands and Vendors"], ["couples", "Couples"]].map(([id, label]) => (
                <button
                  key={id}
                  data-testid={`audience-${id}`}
                  onClick={() => switchAudience(id)}
                  className={`rounded-full px-5 md:px-7 py-2.5 text-[10px] md:text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                    audience === id ? "bg-ink text-cream" : "text-ink/60 hover:text-gold"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {audience === "brands" && (
              <div className="inline-flex bg-white border-2 border-gold/25 rounded-full p-1.5 gap-1" data-testid="billing-toggle">
                {[["monthly", "Monthly"], ["annual", "Annual"]].map(([id, label]) => (
                  <button
                    key={id}
                    data-testid={`billing-${id}`}
                    onClick={() => setBilling(id)}
                    className={`rounded-full px-5 py-2.5 text-[10px] md:text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                      billing === id ? "bg-gold text-ink" : "text-ink/60 hover:text-gold"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Reveal>

        <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid="plan-grid">
          {plans.map((p, i) => {
            const pr = planPrice(p);
            return (
              <Reveal key={p.id} delay={0.07 * i}>
                <button
                  type="button"
                  data-testid={`plan-${p.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setPlan(p.id)}
                  className={`w-full text-left h-full rounded-[2rem] p-7 md:p-8 border-2 transition-all duration-300 ${
                    plan === p.id
                      ? "bg-ink text-cream border-ink shadow-[0_24px_60px_rgba(9,9,11,0.18)] md:-translate-y-1"
                      : "bg-white border-gold/15 hover:border-gold/50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className={`text-[10px] font-bold uppercase tracking-[0.2em] ${plan === p.id ? "text-champagne" : "text-gold"}`}>
                      {p.name}
                    </p>
                    {plan === p.id && (
                      <span className="w-7 h-7 rounded-full bg-champagne text-ink flex items-center justify-center">
                        <Check size={14} />
                      </span>
                    )}
                  </div>
                  <p className={`mt-4 font-display text-4xl md:text-5xl ${plan === p.id ? "text-cream" : "text-ink"}`}>
                    {pr.main}
                    <span className={`text-sm font-body font-medium ml-1 ${plan === p.id ? "text-cream/60" : "text-ink/50"}`}>{pr.sub}</span>
                  </p>
                  <p className={`mt-3 text-sm leading-relaxed ${plan === p.id ? "text-cream/70" : "text-neutral-600"}`}>{p.desc}</p>
                  <ul className="mt-5 space-y-2.5">
                    {p.perks.map((perk) => (
                      <li key={perk} className={`flex items-start gap-2.5 text-xs ${plan === p.id ? "text-cream/80" : "text-ink/70"}`}>
                        <Check size={13} className={`mt-0.5 shrink-0 ${plan === p.id ? "text-champagne" : "text-gold"}`} />
                        {perk}
                      </li>
                    ))}
                  </ul>
                </button>
              </Reveal>
            );
          })}
        </div>

        <div className="max-w-2xl mx-auto mt-16">
          <Reveal>
            <div className="bg-white border-2 border-gold/15 rounded-[2rem] p-6 md:p-12">
              {verifying ? (
                <div className="text-center py-10" data-testid="payment-verifying">
                  <Loader2 size={30} className="mx-auto text-gold animate-spin" />
                  <h2 className="mt-6 font-display uppercase text-2xl text-ink">Confirming your payment</h2>
                  <p className="mt-2 text-sm text-neutral-500">One moment while Stripe confirms.</p>
                </div>
              ) : paidDone ? (
                <div className="text-center py-8" data-testid="payment-success">
                  <span className="inline-flex w-16 h-16 rounded-full bg-blush items-center justify-center">
                    <Check size={26} className="text-gold" />
                  </span>
                  <h2 className="mt-6 font-display uppercase text-3xl text-ink">Payment received</h2>
                  <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                    Welcome aboard. Our editors will reach out within 48 hours to onboard you and schedule your feature.
                  </p>
                </div>
              ) : done ? (
                <div className="text-center py-8" data-testid="featured-success">
                  <span className="inline-flex w-16 h-16 rounded-full bg-blush items-center justify-center">
                    <Check size={26} className="text-gold" />
                  </span>
                  <h2 className="mt-6 font-display uppercase text-3xl text-ink">Request received</h2>
                  <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                    Our editors review every submission personally. If your story or your work is a fit for our pages,
                    we will write back within a few days.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <h2 className="font-display uppercase text-2xl md:text-3xl text-ink">Tell us about you</h2>
                    <Badge tone="petal">{priceLabel()}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-neutral-500">
                    {lookupKey()
                      ? "Secure checkout by Stripe. UPI, cards and netbanking accepted."
                      : "Brands, vendors, venues and couples are all welcome."}
                  </p>
                  <form onSubmit={submit} className="mt-8 space-y-5">
                    <div>
                      <label className={labelCls}>Your Name or Brand</label>
                      <input data-testid="featured-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Designs by Inaara, or Aisha and Rohan" className={inputCls} />
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className={labelCls}>Email</label>
                        <input data-testid="featured-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="you@example.com" className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>You are a</label>
                        <Select testid="featured-kind-select" value={form.kind} onChange={(v) => setForm({ ...form, kind: v })} options={KINDS} />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className={labelCls}>Instagram Handle</label>
                        <input data-testid="featured-instagram-input" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                          placeholder="@yourhandle" className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>City</label>
                        <input data-testid="featured-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                          placeholder="Mumbai" className={inputCls} />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Your Story</label>
                      <textarea data-testid="featured-message-input" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="Tell us about your work or your wedding, and why it belongs on TBOI" className={inputCls} />
                    </div>
                    <button type="submit" data-testid="featured-submit-button" disabled={busy}
                      className="group w-full inline-flex items-center justify-center gap-3 bg-ink text-cream rounded-full px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50">
                      {busy ? "Please wait" : lookupKey() ? `Pay ${priceLabel()} · Secure Checkout` : `Submit · ${plan}`} <Send size={14} />
                    </button>
                  </form>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
